import {
  Card,
  ControllerRenderProps,
  FieldPath,
  FieldValues,
  MultiSelect,
  SelectOption,
  Typography,
} from '@otp/ui';
import { useEffect, useMemo, useState } from 'react';
import { useMostQueries } from 'src/hooks/query/most/use-most-queries';
import { useMostTriages } from 'src/hooks/query/most/use-most-triages';
import {
  MostQuery,
  MostRunCommandType,
  MostTriage,
} from 'src/graphql/generated/graphql';
import { Trans, useTranslation } from 'react-i18next';
import clsx from 'clsx';

type ActionProps<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> = {
  disabled?: boolean;
  className?: string;
  field: ControllerRenderProps<TFieldValues, TName>;
  stores?: string[];
  command?: {
    commandId: string;
    name: string;
    type: MostRunCommandType;
  };
  showDetails?: boolean;
};

export const ActionField = <
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>({
  disabled = false,
  className,
  field,
  stores = [],
  command,
  showDetails = false,
}: ActionProps<TFieldValues, TName>) => {
  const { data: queries, loading: loadingQueries } = useMostQueries();
  const { data: triages, loading: loadingTriages } = useMostTriages();
  const [selectedCommand, setSelectedCommand] = useState<
    Partial<MostQuery> | Partial<MostTriage> | undefined
  >();
  const { t } = useTranslation();

  const defaultValue = useMemo(() => field.value?.commandId, []);

  // Action dropdown can have additional options for case when we should execute suggested command which is not in avaliable commands list
  const extraCommandOptions = command
    ? [
        {
          value: command?.commandId,
          name: command?.name,
          extra: undefined,
          group: command?.type as string,
        },
      ]
    : [];

  const actionOptions = useMemo(() => {
    if (loadingQueries || loadingTriages) {
      return [];
    }

    return queries?.records
      ?.map((entity) => ({
        value: entity.id,
        name: entity.name,
        extra: entity.description,
        group:
          typeof entity.categoryName !== undefined &&
          entity.categoryName !== null
            ? entity.categoryName
            : t('common.other'),
      }))
      .concat(
        triages?.records?.map((entity) => ({
          value: entity.id,
          name: entity.name,
          extra: undefined,
          group: MostRunCommandType.Triage as string,
        })) || [],
      )
      .concat(extraCommandOptions)
      .sort((a, b) => -(b.group as string).localeCompare(a.group as string));
  }, [queries?.records, t, triages?.records, loadingQueries, loadingTriages]);

  useEffect(() => {
    if (!actionOptions?.length) {
      return;
    }

    const defaultSelectedOption = actionOptions.find(
      (option) => option.value === defaultValue,
    );

    if (defaultSelectedOption) {
      onChange(defaultSelectedOption);
    }
  }, [actionOptions, defaultValue]);

  const getAppliesToDataForSelectedAction = (
    commandId: string | number,
    commandType: MostRunCommandType,
  ) => {
    const entity =
      commandType === MostRunCommandType.Triage
        ? triages?.records?.find((item) => item.id === commandId)
        : queries?.records?.find((item) => item.id === commandId);

    if (!entity) {
      return {};
    }

    return {
      appliesToBOS: entity.appliesToBOS,
      appliesToWST: entity.appliesToWST,
      appliesToKVS: entity.appliesToKVS,
      appliesToPOS: entity.appliesToPOS,
      appliesToKIOSK: entity.appliesToKIOSK,
      appliesToESMARTCLOCK: entity.appliesToESMARTCLOCK,
      appliesToEPR: entity.appliesToEPR,
      appliesToRHS: entity.appliesToRHS,
    };
  };

  const onChange = (value: SelectOption) => {
    const type =
      value?.group === MostRunCommandType.Triage
        ? MostRunCommandType.Triage
        : MostRunCommandType.Query;

    let appliesToData = {};
    if (value?.value) {
      appliesToData = getAppliesToDataForSelectedAction(value.value, type);
    }

    field.onChange({
      ...field.value,
      commandId: value?.value,
      description: value?.extra,
      name: value?.name,
      type,
      ...appliesToData,
    });

    setSelectedCommand(
      resolveCommand(
        value?.value as string,
        queries?.records as MostQuery[],
        triages?.records as MostTriage[],
      ),
    );
  };

  const resolveCommand = (
    commandId: string,
    queries: Partial<MostQuery>[],
    triages: Partial<MostTriage>[],
  ) => {
    return (
      queries.find((query) => query.id === commandId) ||
      triages.find((triage) => triage.id === commandId)
    );
  };

  const resolveAppliesTo = (
    command?: Partial<MostQuery> | Partial<MostTriage>,
  ) => {
    const defaultValue = 'none';

    if (!command) {
      return defaultValue;
    }

    if (command.appliesToBOS) {
      return 'bos';
    }

    if (command.appliesToEPR) {
      return 'epr';
    }

    if (command.appliesToESMARTCLOCK) {
      return 'esmartlock';
    }

    if (command.appliesToKIOSK) {
      return 'kiosk';
    }

    if (command.appliesToKVS) {
      return 'kvs';
    }

    if (command.appliesToPOS) {
      return 'pos';
    }

    if (command.appliesToRHS) {
      return 'rhs';
    }

    if (command.appliesToWST) {
      return 'wst';
    }

    return defaultValue;
  };

  const resolveFeatured = (
    command?: Partial<MostQuery> | Partial<MostTriage>,
  ) => 'yes';

  return (
    <>
      <MultiSelect
        multiple={false}
        fullWidth={true}
        className={className}
        options={actionOptions || []}
        shouldGroup={true}
        getOptionDisabled={(option) =>
          option.group === (MostRunCommandType.Triage as string) &&
          stores?.length > 1
        }
        disabled={disabled || loadingQueries || loadingTriages}
        defaultValue={defaultValue}
        {...field}
        onChange={(_event, value) => {
          onChange(value);
        }}
        value={field.value?.commandId || null}
        ref={undefined}
      />
      {showDetails && selectedCommand && (
        <Card className="mt-2 w-auto h-auto">
          {
            <div
              className={clsx('grid', 'md:grid-cols-12 grid-cols-7', 'gap-4')}
            >
              <Typography variant="subtitle1" className="col-span-2">
                {t('most.list.schedule.form.details.featured')}
              </Typography>
              <Typography
                color="var(--mui-palette-primary-900)"
                variant="subtitle1"
                className={clsx('font-bold')}
              >
                {t(
                  `most.list.schedule.form.details.featured-value.${resolveFeatured(
                    selectedCommand,
                  )}`,
                )}
              </Typography>
            </div>
          }
          {
            <div
              className={clsx('grid', 'md:grid-cols-12 grid-cols-7', 'gap-4')}
            >
              <Typography variant="subtitle1" className="col-span-2 ">
                {t('most.list.schedule.form.details.applies-to')}
              </Typography>
              <Typography
                color="var(--mui-palette-primary-900)"
                variant="subtitle1"
                className={clsx('font-bold')}
              >
                {t(
                  `most.list.schedule.form.details.applies-to-value.${resolveAppliesTo(
                    selectedCommand,
                  )}`,
                )}
              </Typography>
            </div>
          }
          {(selectedCommand as MostQuery).description && (
            <Typography variant="subtitle1" className="mt-2">
              <Trans
                t={t}
                i18nKey="most.list.schedule.form.details.description"
                values={{
                  description: (selectedCommand as MostQuery).description,
                }}
              />
            </Typography>
          )}
        </Card>
      )}
    </>
  );
};
